/**
 * useMetrics Hook - Admin dashboard metrics (real backend).
 *
 * Centralized polling via Zustand store to avoid duplicate intervals when
 * multiple dashboard widgets subscribe in the same page.
 */

import { useCallback, useEffect } from "react";
import { useMetricsStore } from "@/shared/stores/metricsStore";

export type { SystemMetrics, GamingMetrics, DashboardMetrics } from "@/shared/stores/metricsStore";

export interface SystemHealth {
    cpu: { level: string; value: number };
    memory: { level: string; value: number };
    disk: { level: string; value: number };
    overall: string;
}

export function useMetrics() {
    const metrics = useMetricsStore((s) => s.metrics);
    const isLoading = useMetricsStore((s) => s.isLoading);
    const error = useMetricsStore((s) => s.error);
    const startPolling = useMetricsStore((s) => s.startPolling);
    const stopPolling = useMetricsStore((s) => s.stopPolling);
    const refresh = useMetricsStore((s) => s.refresh);

    const getSystemHealth = useCallback((): SystemHealth | null => {
        if (!metrics?.system) return null;

        const getHealthLevel = (
            value: number,
            warningThreshold: number,
            criticalThreshold: number,
        ) => {
            if (value >= criticalThreshold) return "critical";
            if (value >= warningThreshold) return "warning";
            return "good";
        };

        const cpuHealth = getHealthLevel(metrics.system.cpu_percent, 70, 90);
        const memoryHealth = getHealthLevel(metrics.system.memory_percent, 80, 95);
        const diskHealth = getHealthLevel(metrics.system.disk_percent, 85, 95);

        const overallHealth = [cpuHealth, memoryHealth, diskHealth].includes(
            "critical",
        )
            ? "critical"
            : [cpuHealth, memoryHealth, diskHealth].includes("warning")
              ? "warning"
              : "good";

        return {
            cpu: { level: cpuHealth, value: metrics.system.cpu_percent },
            memory: { level: memoryHealth, value: metrics.system.memory_percent },
            disk: { level: diskHealth, value: metrics.system.disk_percent },
            overall: overallHealth,
        };
    }, [metrics]);

    const getGamingMetrics = useCallback(() => {
        return metrics?.gaming || null;
    }, [metrics]);

    const formatBytes = useCallback((bytes: number): string => {
        if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
        const k = 1024;
        const sizes = ["B", "KB", "MB", "GB", "TB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    }, []);

    useEffect(() => {
        startPolling();
        return () => stopPolling();
    }, [startPolling, stopPolling]);

    return {
        metrics,
        isLoading,
        error,
        refresh,
        getSystemHealth,
        getGamingMetrics,
        formatBytes,
    };
}
